import { useState, useEffect, useRef } from 'react';
import { 
  FiShield, FiMapPin, FiAward, FiClock, FiUsers, FiStar, FiTrendingUp, 
  FiCheckCircle, FiHeart, FiDollarSign
} from 'react-icons/fi';
import { GiTrophy, GiMedal, GiDiamondRing, GiSparkles } from 'react-icons/gi';
import { FaHandshake, FaRegSmile, FaBuilding } from 'react-icons/fa';

const WhyUs = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
      }
    };
    
    const section = sectionRef.current;
    if (section) {
      section.addEventListener('mousemove', handleMouseMove);
    }
    
    return () => {
      if (section) {
        section.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  const features = [
    {
      icon: FaBuilding,
      title: 'In-House Construction',
      description: 'Complete vertical integration ensuring highest quality standards across all projects',
      stat: '15+ Years',
      statLabel: 'Experience'
    },
    {
      icon: GiSparkles,
      title: 'Impeccable Craftsmanship',
      description: 'Attention to detail in every aspect of construction and design with premium materials',
      stat: '1000+',
      statLabel: 'Homes Delivered'
    },
    {
      icon: FiStar,
      title: 'Sustainable Living',
      description: 'Eco-friendly practices and green building certifications for healthier living',
      stat: 'IGBC Gold',
      statLabel: 'Certified'
    },
    {
      icon: FiShield,
      title: 'Trust & Reliability',
      description: 'Legacy of delivering projects on time with complete transparency and integrity',
      stat: '100%',
      statLabel: 'Delivery Record'
    },
    {
      icon: FiMapPin,
      title: 'Prime Locations',
      description: 'Strategic locations with excellent connectivity and future growth potential',
      stat: '8+ Cities',
      statLabel: 'Present'
    },
    {
      icon: FiAward,
      title: 'Award-Winning Design',
      description: 'Recognized for architectural excellence and innovative design solutions',
      stat: '25+ Awards',
      statLabel: 'Won'
    },
    {
      icon: FiClock,
      title: 'Timely Delivery',
      description: '100% track record of delivering projects before or on schedule',
      stat: 'Early Delivery',
      statLabel: 'Track Record'
    },
    {
      icon: FiUsers,
      title: 'Customer First',
      description: 'Dedicated customer support throughout your home buying journey',
      stat: '5000+',
      statLabel: 'Happy Families'
    }
  ];

  const trustBadges = [
    { icon: GiTrophy, label: '25+ Awards', color: 'from-gold to-amber-500' },
    { icon: FaHandshake, label: '10,000+ Customers', color: 'from-emerald-500 to-teal-500' },
    { icon: GiMedal, label: 'ISO Certified', color: 'from-blue-500 to-indigo-500' },
    { icon: FaRegSmile, label: '98% Satisfaction', color: 'from-purple-500 to-pink-500' }
  ];

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes whyusFadeUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes whyusScaleUp {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }
      @keyframes whyusShimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes whyusPulse {
        0%, 100% { opacity: 0.15; transform: scale(1); }
        50% { opacity: 0.3; transform: scale(1.05); }
      }
      .animate-whyus-fade-up { animation: whyusFadeUp 0.8s ease-out forwards; }
      .animate-whyus-scale-up { animation: whyusScaleUp 0.5s ease-out forwards; opacity: 0; }
      .animate-whyus-shimmer {
        background: linear-gradient(90deg, #D4AF37, #FFD700, #D4AF37);
        background-size: 200% auto;
        -webkit-background-clip: text;
        background-clip: text;
        animation: whyusShimmer 3s linear infinite;
      }
      .animate-whyus-pulse { animation: whyusPulse 4s ease-in-out infinite; }
      .delay-100 { animation-delay: 0.1s; }
      .delay-200 { animation-delay: 0.2s; }
      .delay-300 { animation-delay: 0.3s; }
      .delay-400 { animation-delay: 0.4s; }
      .delay-500 { animation-delay: 0.5s; }
      .font-serif { font-family: 'Cormorant Garamond', serif; }
      .font-montserrat { font-family: 'Montserrat', sans-serif; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <section 
      id="why-us" 
      ref={sectionRef}
      className="relative py-16 sm:py-20 md:py-28 lg:py-32 overflow-hidden bg-navy"
    >
      {/* Ambient Background - Matching Hero Section */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/95 to-navy"></div>
        
        {/* Animated gradient orb that follows mouse */}
        <div 
          className="absolute w-[20rem] h-[20rem] sm:w-[30rem] sm:h-[30rem] md:w-[40rem] md:h-[40rem] bg-gold/5 rounded-full blur-3xl transition-transform duration-300 hidden md:block"
          style={{
            left: `${mousePosition.x}%`,
            top: `${mousePosition.y}%`,
            transform: 'translate(-50%, -50%)'
          }}
        />
        
        {/* Static golden orbs */}
        <div className="absolute top-0 left-0 w-[18rem] h-[18rem] sm:w-[25rem] sm:h-[25rem] md:w-[30rem] md:h-[30rem] bg-gold/5 rounded-full blur-3xl animate-whyus-pulse"></div>
        <div className="absolute bottom-0 right-0 w-[15rem] h-[15rem] sm:w-[20rem] sm:h-[20rem] md:w-[25rem] md:h-[25rem] bg-amber-500/5 rounded-full blur-3xl animate-whyus-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Floating particles */}
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-gold/20 rounded-full animate-whyus-pulse"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${Math.random() * 10 + 8}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-8 lg:px-12 z-10">
        {/* Section Header - Matching Hero Typography */}
        <div className="text-center mb-12 sm:mb-16 animate-whyus-fade-up">
          <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
            <div className="w-6 sm:w-8 h-px bg-gold"></div>
            <span className="text-gold text-[9px] sm:text-[10px] md:text-[11px] font-semibold tracking-[0.2em] sm:tracking-[0.24em] md:tracking-[0.28em] uppercase font-montserrat">Why Choose Us</span>
            <div className="w-6 sm:w-8 h-px bg-gold"></div>
          </div>
          
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-3 sm:mb-4">
            Setting New Benchmarks in
            <span className="block text-gold font-normal italic mt-1 sm:mt-2">Luxury Real Estate</span>
          </h2>
          
          <div className="w-12 sm:w-16 h-px bg-gold/50 mx-auto mt-5 sm:mt-6 mb-5 sm:mb-6"></div>
          
          <p className="text-gray-300 max-w-3xl mx-auto text-xs sm:text-sm md:text-base leading-relaxed font-light px-4">
            Through unwavering commitment to excellence, we create living spaces that inspire and endure. 
            Our passion for perfection reflects in every project we undertake.
          </p>
        </div>

        {/* Features Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mb-12 sm:mb-16 md:mb-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group animate-whyus-scale-up"
              style={{ animationDelay: `${index * 0.05}s` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="relative bg-black/30 backdrop-blur-sm p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border border-white/10 hover:border-gold/30 transition-all duration-500 hover:transform hover:-translate-y-2 overflow-hidden h-full">
                {/* Animated background icon */}
                <div className="absolute -right-6 -top-6 w-20 h-20 sm:w-24 sm:h-24 opacity-0 group-hover:opacity-10 transition-opacity duration-500">
                  <feature.icon size={80} className="sm:text-[100px]" />
                </div>
                
                {/* Icon */}
                <div className="relative mb-4 sm:mb-5">
                  <div className="absolute inset-0 bg-gold/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl bg-gold/15 flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300 border border-gold/30">
                    <feature.icon className="text-gold w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-2 sm:mb-3 group-hover:text-gold transition-colors font-serif">
                  {feature.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-300 text-[11px] sm:text-xs md:text-sm leading-relaxed mb-3 sm:mb-4">
                  {feature.description}
                </p>
                
                {/* Stat */}
                <div className="pt-2 sm:pt-3 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-base sm:text-lg md:text-xl font-bold text-gold">{feature.stat}</span>
                    <span className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wider">{feature.statLabel}</span>
                  </div>
                </div>
                
                {/* Animated underline */}
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-gold to-amber-500 group-hover:w-full transition-all duration-500"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges Row */}
        <div className="mb-12 sm:mb-16 animate-whyus-fade-up delay-200">
          <div className="bg-black/30 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 border border-white/10">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              {trustBadges.map((badge, idx) => (
                <div key={idx} className="text-center group cursor-pointer">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto rounded-full bg-gradient-to-r ${badge.color} flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                    <badge.icon size={20} className="text-white sm:text-2xl md:text-3xl" />
                  </div>
                  <p className="text-white font-semibold text-[10px] sm:text-xs md:text-sm">{badge.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Section - Key Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-12 sm:mb-16">
          {[
            { value: '500+', label: 'Luxury Homes', icon: FiUsers },
            { value: '2M+', label: 'Sq. Ft. Area', icon: FiTrendingUp },
            { value: '100%', label: 'Customer Trust', icon: FiHeart },
            { value: '15+', label: 'Years Legacy', icon: FiClock }
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-3 sm:p-4 md:p-6 rounded-xl bg-black/30 backdrop-blur-sm border border-white/10 hover:border-gold/30 transition-all group animate-whyus-scale-up" style={{ animationDelay: `${idx * 0.1}s` }}>
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mx-auto rounded-full bg-gold/15 flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-gold/25 transition-all group-hover:scale-110 border border-gold/30">
                <stat.icon size={14} className="text-gold sm:text-lg md:text-xl" />
              </div>
              <div className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-0.5 sm:mb-1 font-serif">{stat.value}</div>
              <div className="text-[9px] sm:text-xs text-gray-400 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* RERA Badge and CTA */}
        <div className="text-center animate-whyus-fade-up delay-300">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 bg-gradient-to-r from-gold/10 to-amber-500/10 backdrop-blur-sm px-5 sm:px-6 md:px-8 py-5 sm:py-6 rounded-xl sm:rounded-2xl border border-gold/20">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <FiShield size={20} className="text-gold sm:text-2xl md:text-3xl" />
              </div>
              <div className="text-left">
                <div className="text-white font-semibold text-sm sm:text-base md:text-lg font-serif">RERA Registered & Government Approved</div>
                <div className="text-gray-300 text-[9px] sm:text-[10px] md:text-xs">UP-RERA-2024-000123 | All projects RERA compliant</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
              <button className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 md:py-2.5 bg-gold text-navy rounded-full font-semibold hover:bg-gold-dark transition-all transform hover:scale-105 flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs md:text-sm">
                <FiCheckCircle size={12} className="sm:text-sm" />
                Verify RERA
              </button>
              <button className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 md:py-2.5 border border-gold text-gold rounded-full font-semibold hover:bg-gold hover:text-navy transition-all transform hover:scale-105 text-[10px] sm:text-xs md:text-sm">
                Download Brochure
              </button>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-10 sm:mt-12 md:mt-16 pt-6 sm:pt-8 border-t border-white/10 flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 text-center">
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 text-[9px] sm:text-xs">
            <FiCheckCircle size={10} className="text-gold sm:text-xs" />
            <span>ISO 9001:2015 Certified</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 text-[9px] sm:text-xs">
            <FiCheckCircle size={10} className="text-gold sm:text-xs" />
            <span>CRISIL Rated AAA</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 text-[9px] sm:text-xs">
            <FiCheckCircle size={10} className="text-gold sm:text-xs" />
            <span>IGBC Gold Certified</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 text-[9px] sm:text-xs">
            <FiCheckCircle size={10} className="text-gold sm:text-xs" />
            <span>Great Place to Work Certified</span>
          </div>
        </div>

        {/* Bottom Decorative Line */}
        <div className="mt-10 sm:mt-12 md:mt-16 pt-4 sm:pt-6">
          <div className="flex justify-center">
            <div className="w-16 sm:w-20 h-px bg-gold/30"></div>
          </div>
          <p className="text-center text-gray-500 text-[8px] sm:text-[9px] md:text-[10px] mt-4 sm:mt-5 tracking-wider uppercase">
            Excellence Delivered, Trust Earned
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;